/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TRACING_SYSTEM
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * React hook for polling trace status.
 */

import { useState, useEffect } from "react";
import { pollTraceStatus, Trace } from "../services/traces";

interface UseTraceStatusOptions {
  onComplete?: (trace: Trace) => void;
  onFailed?: (trace: Trace) => void;
  onUpdate?: (trace: Trace) => void;
  intervalMs?: number;
  maxAttempts?: number;
}

export function useTraceStatus(traceId: string | null, options: UseTraceStatusOptions = {}) {
  const [trace, setTrace] = useState<Trace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { onComplete, onFailed, onUpdate, intervalMs = 2000, maxAttempts = 60 } = options;

  useEffect(() => {
    if (!traceId) return;

    setLoading(true);

    const cleanup = pollTraceStatus(
      traceId,
      (updatedTrace) => {
        setTrace(updatedTrace);
        setLoading(false);

        // [2025-12-20] Tracing: Call user callbacks
        if (onUpdate) onUpdate(updatedTrace);

        if (updatedTrace.status === "SUCCESS" && onComplete) {
          onComplete(updatedTrace);
        } else if (updatedTrace.status === "FAILED" && onFailed) {
          onFailed(updatedTrace);
          setError(updatedTrace.error?.message || "Operation failed");
        }
      },
      intervalMs,
      maxAttempts,
    );

    // Cleanup on unmount
    return cleanup;
  }, [traceId, onComplete, onFailed, onUpdate, intervalMs, maxAttempts]);

  return { trace, loading, error };
}
